import { useEffect, useMemo, useState } from 'react'
import { Box, Copy, Loader2 } from 'lucide-react'

import { useAppStore } from '@core/store'
import { viewerApi } from '@viewer/lib/viewerApi'
import type { ModeloStep } from '@viewer/lib/step/modeloStep'
import { antepassadosDe, atributosDe, nomeDaClasse, nomeDoInverso } from '@viewer/lib/step/esquemaIfc'
import { analisarValor, argumentoQueReferencia, type ValorStep } from '@viewer/lib/step/valorStep'
import { lerArgTexto } from '@viewer/lib/step/textoStep'
import { depoisDePintar } from '@viewer/dados/modeloEmTexto'
import { AVISO, BTN, NOTA } from '@viewer/dados/estilos'

const MAX_ITENS = 60
const MAX_LINHA = 20000
const MAX_POR_GRUPO = 200

/** "#45 IfcLocalPlacement" e, se a classe tiver Name, o nome. */
function rotuloDe(m: ModeloStep, esquema: string | null, id: number): { classe: string; nome: string | null } {
  const c = m.classe(id)
  if (!c) return { classe: '(não existe)', nome: null }
  const i = atributosDe(esquema, c)?.indexOf('Name') ?? -1
  return { classe: nomeDaClasse(c), nome: i >= 0 ? lerArgTexto(m.args(id)[i]) : null }
}

export function LigacaoRef({
  m,
  esquema,
  id,
  ir,
  compacto,
}: {
  m: ModeloStep
  esquema: string | null
  id: number
  ir: (id: number) => void
  compacto?: boolean
}) {
  const r = rotuloDe(m, esquema, id)
  return (
    <button
      type="button"
      onClick={() => ir(id)}
      className="inline-flex max-w-full items-baseline gap-1 truncate rounded px-0.5 text-left hover:bg-[var(--color-accent-bg)]"
      title={`Ir para #${id}`}
    >
      <span className="font-mono text-[var(--color-accent)]">#{id}</span>
      {!compacto && <span className="text-[var(--color-text-muted)]">{r.classe}</span>}
      {r.nome && <span className="truncate text-[var(--color-text)]">{r.nome}</span>}
    </button>
  )
}

function Valor({ v, m, esquema, ir }: { v: ValorStep; m: ModeloStep; esquema: string | null; ir: (id: number) => void }) {
  const [tudo, setTudo] = useState(false)
  switch (v.tipo) {
    case 'vazio':
      return <span className="font-mono text-[var(--color-text-dim)]" title="Sem valor ($)">$</span>
    case 'derivado':
      return <span className="font-mono text-[var(--color-text-dim)]" title="Atributo derivado: o valor calcula-se, não se escreve (*)">*</span>
    case 'ref':
      return <LigacaoRef m={m} esquema={esquema} id={v.id} ir={ir} />
    case 'texto':
      return <span className="break-all text-[var(--color-text)]">'{v.valor}'</span>
    case 'enum':
      return <span className="font-mono text-[var(--color-text)]">.{v.valor}.</span>
    case 'numero':
    case 'binario':
      return <span className="font-mono text-[var(--color-text)]">{v.bruto}</span>
    case 'desconhecido':
      return <span className="font-mono text-[var(--color-warn-text)]" title="Forma não reconhecida: mostra-se como está">{v.bruto}</span>
    case 'tipado':
      return (
        <span>
          <span className="text-[var(--color-text-muted)]">{nomeDaClasse(v.classe)}(</span>
          <Valor v={v.valor} m={m} esquema={esquema} ir={ir} />
          <span className="text-[var(--color-text-muted)]">)</span>
        </span>
      )
    case 'lista': {
      if (v.itens.length === 0) return <span className="font-mono text-[var(--color-text-dim)]">()</span>
      const so = tudo ? v.itens : v.itens.slice(0, MAX_ITENS)
      const soRefs = v.itens.every((x) => x.tipo === 'ref')
      return (
        <span className={soRefs ? 'flex flex-col' : 'inline'}>
          {!soRefs && <span className="text-[var(--color-text-dim)]">(</span>}
          {so.map((x, i) => (
            <span key={i}>
              <Valor v={x} m={m} esquema={esquema} ir={ir} />
              {!soRefs && i < so.length - 1 && <span className="text-[var(--color-text-dim)]">, </span>}
            </span>
          ))}
          {v.itens.length > so.length && (
            <button type="button" className="text-[var(--color-accent)] hover:underline" onClick={() => setTudo(true)}>
              {' '}e mais {v.itens.length - so.length}
            </button>
          )}
          {!soRefs && <span className="text-[var(--color-text-dim)]">)</span>}
          {soRefs && <span className="text-[9px] text-[var(--color-text-dim)]">{v.itens.length} referência(s)</span>}
        </span>
      )
    }
  }
}

interface GrupoInverso {
  chave: string
  inverso: string | null
  atributo: string
  origens: number[]
}

function agruparInversos(m: ModeloStep, esquema: string | null, id: number, origens: number[]): GrupoInverso[] {
  const classe = m.classe(id) ?? ''
  const grupos = new Map<string, GrupoInverso>()
  for (const o of origens) {
    const co = m.classe(o) ?? ''
    const idx = argumentoQueReferencia(m.args(o), id)
    const nomeAttr = idx >= 0 ? (atributosDe(esquema, co)?.[idx] ?? `atributo ${idx + 1}`) : 'dentro de um valor'
    const atributo = `${nomeDaClasse(co)}.${nomeAttr}`
    const inverso = idx >= 0 ? nomeDoInverso(esquema, classe, co, idx) : null
    const chave = `${inverso ?? ''}\u0001${atributo}`
    const g = grupos.get(chave)
    if (g) g.origens.push(o)
    else grupos.set(chave, { chave, inverso, atributo, origens: [o] })
  }
  // Primeiro os que o esquema declara como inversos, depois os outros.
  return [...grupos.values()].sort(
    (a, b) => Number(!a.inverso) - Number(!b.inverso) || (a.inverso ?? a.atributo).localeCompare(b.inverso ?? b.atributo),
  )
}

function Grupo({ g, m, esquema, ir }: { g: GrupoInverso; m: ModeloStep; esquema: string | null; ir: (id: number) => void }) {
  const [n, setN] = useState(MAX_POR_GRUPO)
  return (
    <div className="flex flex-col">
      <div className="text-[10px]">
        {g.inverso ? (
          <>
            <span className="font-medium text-[var(--color-text)]">{g.inverso}</span>{' '}
            <span className="text-[var(--color-text-muted)]">por {g.atributo}</span>
          </>
        ) : (
          <span className="text-[var(--color-text)]" title="O esquema não declara um atributo inverso para esta referência">{g.atributo}</span>
        )}{' '}
        <span className="font-mono text-[var(--color-text-dim)]">({g.origens.length})</span>
      </div>
      <div className="flex flex-col pl-3">
        {g.origens.slice(0, n).map((o) => (
          <LigacaoRef key={o} m={m} esquema={esquema} id={o} ir={ir} />
        ))}
        {g.origens.length > n && (
          <button type="button" className="self-start text-[10px] text-[var(--color-accent)] hover:underline" onClick={() => setN(n + MAX_POR_GRUPO)}>
            Mostrar mais ({g.origens.length - n})
          </button>
        )}
      </div>
    </div>
  )
}

export default function VistaEntidade({
  m,
  modelId,
  id,
  ir,
}: {
  m: ModeloStep
  modelId: string
  id: number
  ir: (id: number) => void
}) {
  const esquema = m.esquema()
  const classe = m.classe(id)
  const [inversos, setInversos] = useState<{ m: ModeloStep; id: number; grupos: GrupoInverso[] } | null>(null)
  const aContar = !!classe && (inversos?.id !== id || inversos.m !== m)

  useEffect(() => {
    let vivo = true
    if (!classe) return
    // A primeira vez percorre o ficheiro todo: deixa pintar antes.
    void depoisDePintar().then(() => {
      if (!vivo) return
      setInversos({ m, id, grupos: agruparInversos(m, esquema, id, m.referenciadoPor(id)) })
    })
    return () => {
      vivo = false
    }
  }, [m, esquema, id, classe])

  const dados = useMemo(() => {
    if (!classe) return null
    const args = m.args(id)
    const nomes = atributosDe(esquema, classe)
    return {
      args,
      nomes,
      valores: args.map(analisarValor),
      antepassados: antepassadosDe(esquema, classe).map(nomeDaClasse),
      linha: m.linha(id) ?? '',
    }
  }, [m, esquema, id, classe])

  if (!classe || !dados) return <div className={AVISO}>Não há nenhuma entidade #{id} neste ficheiro.</div>

  const copiar = () => {
    void navigator.clipboard?.writeText(dados.linha).then(
      () => useAppStore.getState().pushToast(`Linha #${id} copiada.`, { type: 'success' }),
      () => useAppStore.getState().pushToast('O browser não deixou copiar.', { type: 'error' }),
    )
  }

  const grupos = !aContar ? (inversos?.grupos ?? null) : null
  const totalInv = grupos?.reduce((n, g) => n + g.origens.length, 0) ?? 0

  return (
    <div className="flex flex-col gap-2 text-[11px]">
      <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
        <span className="font-mono text-sm text-[var(--color-accent)]">#{id}</span>
        <span className="text-sm font-medium text-[var(--color-text)]">{nomeDaClasse(classe)}</span>
        <div className="ml-auto flex gap-1">
          <button
            type="button"
            className={BTN}
            onClick={() => viewerApi.selectByLocalId(modelId, id, true)}
            title="Seleccionar no 3D e enquadrar (só faz efeito em elementos com geometria)"
          >
            <Box size={12} /> Mostrar no 3D
          </button>
          <button type="button" className={BTN} onClick={copiar} title="Copiar a linha STEP">
            <Copy size={12} /> Copiar linha
          </button>
        </div>
      </div>
      {dados.antepassados.length > 0 && (
        <div className="text-[10px] text-[var(--color-text-dim)]" title="Herança pelo esquema do ficheiro">
          {dados.antepassados.join(' › ')} › <span className="text-[var(--color-text-muted)]">{nomeDaClasse(classe)}</span>
        </div>
      )}
      {!dados.nomes && (
        <div className={AVISO}>
          {esquema ? `O esquema ${esquema} ou a classe ${classe} não estão nas tabelas do web-ifc` : 'O ficheiro não declara o esquema'}: os
          atributos ficam numerados, sem nome.
        </div>
      )}
      {dados.nomes && dados.nomes.length !== dados.args.length && (
        <div className={AVISO}>
          A linha tem {dados.args.length} argumento(s) e o esquema {esquema} declara {dados.nomes.length} para{' '}
          {nomeDaClasse(classe)}. Os nomes podem não corresponder; a linha STEP em baixo é a fonte.
        </div>
      )}

      <table className="w-full border-collapse">
        <tbody>
          {dados.valores.map((v, i) => {
            const nome = dados.nomes ? dados.nomes[i] : undefined
            return (
              <tr key={i} className="border-t border-[var(--color-border)]/50 align-top">
                <td className="w-6 py-0.5 pr-1 text-right font-mono text-[10px] text-[var(--color-text-dim)]">{i + 1}</td>
                <td className="w-40 py-0.5 pr-2 text-[var(--color-text-muted)]">
                  {nome === null ? <span className="italic" title="Atributo derivado no esquema">(derivado)</span> : (nome ?? `atributo ${i + 1}`)}
                </td>
                <td className="min-w-0 py-0.5">
                  <Valor v={v} m={m} esquema={esquema} ir={ir} />
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>

      <div className="mt-1 flex items-center gap-1 text-[10px] uppercase tracking-wider text-[var(--color-text-muted)]">
        Referenciado por {grupos && <span className="font-mono normal-case">({totalInv})</span>}
        {aContar && <Loader2 size={11} className="animate-spin" />}
      </div>
      {aContar && !grupos && <p className={NOTA}>A primeira consulta percorre o ficheiro todo; as seguintes são imediatas.</p>}
      {grupos && grupos.length === 0 && <p className={NOTA}>Nenhuma entidade aponta para esta.</p>}
      {grupos && grupos.length > 0 && (
        <div className="flex flex-col gap-1.5">
          {grupos.map((g) => (
            <Grupo key={g.chave} g={g} m={m} esquema={esquema} ir={ir} />
          ))}
        </div>
      )}

      <div className="mt-1 text-[10px] uppercase tracking-wider text-[var(--color-text-muted)]">Linha STEP</div>
      <pre className="max-h-48 overflow-auto whitespace-pre-wrap break-all rounded border border-[var(--color-border)] bg-[var(--color-bg)] p-1.5 font-mono text-[10px] text-[var(--color-text)]">
        {dados.linha.length > MAX_LINHA ? `${dados.linha.slice(0, MAX_LINHA)}…` : dados.linha}
      </pre>
      {dados.linha.length > MAX_LINHA && (
        <p className={NOTA}>
          A linha tem {dados.linha.length.toLocaleString('pt-PT')} caracteres; mostram-se os primeiros{' '}
          {MAX_LINHA.toLocaleString('pt-PT')}. "Copiar linha" copia-a inteira.
        </p>
      )}
    </div>
  )
}
